/**
 * Slash command registry - Claude-Code-style "/" commands typed into the chat composer.
 *
 * Commands are DATA, not code (see CLAUDE.md "Reusable primitives"): adding a command is
 * one entry here plus one runtime skill. The web uses this list for the composer menu
 * (filtered by the admin gate client-side); the API re-validates the same definition on
 * start_run (existence + admin gate) and routes deterministically to `skillKey`,
 * bypassing LLM intent classification. Runtime authorization additionally flows through
 * the capability catalog via the skill's own metadata (requiresWorkspaceAdmin /
 * requiredRole), so a forged commandKey can never reach a gated skill. Commands run in
 * the user's selected chat mode; `autoMode` declares what 'auto' collapses to per command.
 */
/** Menu grouping, in display order. Categories are data too: adding one is an entry here.
 * A category with no commands never renders (headers come from the command list), so
 * 'deal' is safe to declare ahead of its first command. */
export const SLASH_COMMAND_CATEGORIES = [
    { key: 'deal', label: 'Deal' },
    { key: 'utility', label: 'Utility' },
    { key: 'workspace-admin', label: 'Workspace admin' },
];
export const SLASH_COMMANDS = [
    {
        key: 'create-deal',
        label: '/create-deal',
        description: 'Guided deal setup: pick the client company, enrich it, optionally analyze a transcript',
        category: 'deal',
        stage: 'alpha',
        skillKey: 'deal_create',
        // auto -> agent: the wizard confirms the company via ask_user before creating
        // anything (and enrichment/transcript only PROPOSE), so agent execution is safe
        // even when the mode picker is left on 'auto'. Sales manager/admin only - the
        // skill's metadata.requiredRole gates it server-side (menu shows it to all sales
        // users, non-eligible runs get the graceful denied-capability reply).
        autoMode: 'agent',
        initialPrompt: 'Create a new deal: ask me which client company it is for (name, website, or pasted ' +
            'context), create the deal, enrich the company, then ask whether to start manually or ' +
            'analyze a meeting transcript.',
    },
    {
        key: 'enrich-company',
        label: '/enrich-company',
        description: "Research this deal's company and fill matching questionnaire fields",
        category: 'deal',
        // GA (no stage): always available. Replaces the old deal-page three-dot "Enrich company".
        skillKey: 'sales_company_enrichment',
        // auto -> agent: enrichment only PROPOSES a review card (the user clicks Fill to apply);
        // no data write happens without confirmation, so agent execution is safe on 'auto'. This
        // is a deliberate manual re-run and is NOT subject to the auto-skip enrichment gate.
        autoMode: 'agent',
        initialPrompt: "Enrich the company for this deal: research it and fill the matching questionnaire fields.",
    },
    {
        key: 'upload-document',
        label: '/upload-document',
        description: 'Upload a meeting transcript or document and extract deal answers from it',
        category: 'deal',
        // GA (no stage): always available. Replaces the old deal-page three-dot "Upload document".
        skillKey: 'deal_transcript_analysis',
        // auto -> ask: the skill previews extracted values in a review card before they apply,
        // matching confirm-or-override. The skill prompts for the file upload in-chat.
        autoMode: 'ask',
        initialPrompt: 'Analyze a document for this deal: let me upload a meeting transcript or document, then ' +
            'extract answers from it and fill the matching questionnaire fields for me to review.',
    },
    {
        key: 'find-problems',
        label: '/find-problems',
        description: 'Analyze discovery notes and extract prioritised, evidence-backed problems',
        category: 'deal',
        stage: 'beta',
        skillKey: 'salesforce_problem_detection',
        autoMode: 'ask',
        initialPrompt: 'Analyze the discovery notes for this Salesforce BPR engagement and extract prioritised, evidence-backed problems for consultant review.',
    },
    {
        key: 'propose-solutions',
        label: '/propose-solutions',
        description: 'Propose 1 to 3 solution options per accepted problem from the pattern menu',
        category: 'deal',
        stage: 'beta',
        skillKey: 'salesforce_solution_options',
        autoMode: 'ask',
        initialPrompt: 'Propose solution options for each confirmed problem in this Salesforce BPR engagement against the solution pattern menu.',
    },
    {
        key: 'explain-estimate',
        label: '/explain-estimate',
        description: "Explain how this deal's estimate was calculated. Optionally name a section, subsection, or line item to focus on",
        category: 'deal',
        stage: 'alpha',
        skillKey: 'explain_estimate',
        // Read-only conversational explainer - nothing to stage/confirm, so auto -> agent.
        // Available to all sales roles (the skill sets no requiredRole). A trailing
        // argument ("/explain-estimate testing") arrives as the prompt and becomes the focus.
        autoMode: 'agent',
        initialPrompt: "Explain this deal's estimate: walk me through the main drivers of the hours and how each " +
            'section was calculated, in plain business language.',
    },
    {
        key: 'merge-questions',
        label: '/merge-questions',
        description: 'Combine several ad-hoc scope questions into one - across groups, optionally under a new group - without losing context; the merged hours are harmonized so the estimate is never inflated.',
        category: 'deal',
        stage: 'beta',
        skillKey: 'adhoc_merge_questions',
        // auto -> agent: the skill confirms which questions merge via ask_user before acting, and the
        // merge tool is high-side-effect (always staged in auto). Collaborator+ and the ai.adhoc_merge_enabled
        // beta gate are re-enforced server-side in the facade.
        autoMode: 'agent',
        initialPrompt: 'Merge ad-hoc scope questions on this deal: show me the questions, let me pick which to ' +
            'combine into one, choose the group, and confirm before merging.',
    },
    {
        key: 'compact',
        label: '/compact',
        description: 'Summarize earlier messages to free up context space',
        category: 'utility',
        stage: 'beta',
        skillKey: 'conversation_compact',
        initialPrompt: 'Compact this conversation: summarize the earlier messages.',
    },
    {
        key: 'init-workspace',
        label: '/init-workspace',
        description: 'Guided setup: workspace logos, storage integration, folder layout',
        category: 'workspace-admin',
        requiresWorkspaceAdmin: true,
        stage: 'alpha',
        skillKey: 'workspace_init',
        // auto → agent: the wizard already asks for confirmation at every step
        // (one ask_user per step, every step skippable), so agent execution is
        // not harmful even when the mode picker is left on 'auto'.
        autoMode: 'agent',
        initialPrompt: 'Initialize this workspace: walk me through branding and storage setup step by step.',
    },
    {
        key: 'create-deal-category',
        label: '/create-deal-category',
        description: 'Create a new deal category: review existing categories, get AI-researched name suggestions, then tailor questions, estimate hours, roles & rates, and the resource plan - each via AI-proposed options you can refine and confirm. Lands as drafts.',
        category: 'workspace-admin',
        requiresWorkspaceAdmin: true,
        stage: 'alpha',
        skillKey: 'deal_category_authoring',
        // auto -> agent: the flow confirms the category name (ask_user), every data op is
        // staged for confirmation (high/low side-effect), and the chain lands as DRAFTS, so
        // agent execution is safe even on 'auto'.
        autoMode: 'agent',
        initialPrompt: 'Create a new deal category: ask which product it is for (if more than one), show me the ' +
            'existing categories and confirm I want to proceed, suggest typical category names to pick ' +
            'from (or let me type one), then tailor it topic by topic - questions, estimate hours, ' +
            'roles & rates, and the resource-plan type - each time letting me pick the source(s) ' +
            '(standard / past deals / AI Research / custom), showing me an AI proposal I can refine, ' +
            'and confirming before moving on. Clone the base setup as drafts I can review and publish.',
    },
    {
        key: 'generate-sow-template',
        label: '/generate-sow-template',
        description: 'Create a reusable SOW template from 3-5 example SOW documents',
        category: 'workspace-admin',
        requiresWorkspaceAdmin: true,
        // alpha: visible only where the workspace min stage is alpha (local/dev), matching the
        // SOW Template Studio gate (SOW_STUDIO_STAGE). Hidden in uat/prod.
        stage: 'alpha',
        skillKey: 'sow_template_generation',
        // auto → agent: the wizard confirms every consequential step (section types,
        // placeholder mapping) and saves as a DRAFT, so agent execution is safe.
        autoMode: 'agent',
        initialPrompt: 'Create a reusable SOW template for this workspace from example SOW documents: ' +
            'walk me through category selection, upload, style choice, section types, and ' +
            'placeholder mapping.',
    },
    {
        key: 'generate-questionnaire-template',
        label: '/generate-questionnaire-template',
        description: 'Build a whole deal-questionnaire template version from a description',
        // Authoring a reusable questionnaire template version is a workspace-admin task (it
        // forks the open deal's template into a new version). Admin-gated here and re-enforced
        // server-side; the skill's metadata.requiresWorkspaceAdmin is the capability-catalog gate.
        category: 'workspace-admin',
        requiresWorkspaceAdmin: true,
        stage: 'alpha',
        skillKey: 'questionnaire_template_generation',
        // auto → agent: the flow confirms the full field plan and version label before building,
        // and creates a DRAFT version (publish stays admin-gated), so agent execution is safe.
        autoMode: 'agent',
        initialPrompt: 'Build a new questionnaire template version for this deal: ask what the questionnaire ' +
            'should capture, propose the sections and fields, confirm the plan with me, then create ' +
            'the version.',
    },
    {
        key: 'import-reference-estimates',
        label: '/import-reference-estimates',
        description: 'Upload past improvement-estimator spreadsheets to build a reference corpus that grounds AI-mode continuous-improvement estimates',
        category: 'workspace-admin',
        requiresWorkspaceAdmin: true,
        stage: 'beta',
        skillKey: 'reference_estimate_import',
        // auto -> agent: the flow confirms product/category and every save via ask_user, and the
        // save tool stages in ask mode, so agent execution is safe even on 'auto'.
        autoMode: 'agent',
        initialPrompt: 'Import reference estimates: ask which product and deal category the corpus is for, let ' +
            'me upload one or more past improvement-estimator spreadsheets, parse each, and after I ' +
            'confirm, save them to the workspace reference corpus.',
    },
];
export function findSlashCommand(key) {
    return SLASH_COMMANDS.find((command) => command.key === key);
}
/**
 * Feature maturity stages, least → most mature. A command's `stage` (absent = 'ga')
 * and a workspace's min-stage threshold are compared by their index here, so the order
 * is the contract: alpha < beta < ga.
 */
export const FEATURE_STAGE_ORDER = ['alpha', 'beta', 'ga'];
/** Rank of a stage by maturity. Unknown/absent → 'ga' (most mature, always available). */
export function stageRank(stage) {
    const idx = FEATURE_STAGE_ORDER.indexOf(stage);
    return idx === -1 ? FEATURE_STAGE_ORDER.indexOf('ga') : idx;
}
/**
 * Generic stage gate: an item marked with `stage` (absent = 'ga') is available when its
 * rank is at or above the workspace's min stage. `workspaceMinStage` absent/unknown
 * defaults to 'alpha' (show everything = pre-gating behavior), so alpha-marked items show
 * only where the workspace min stage is alpha (local/dev; uat/prod seed 'beta'). Reused by
 * non-command composer surfaces (model tiers, Ask/Agent/Auto mode options).
 */
export function isStageAvailable(stage, workspaceMinStage) {
    const threshold = workspaceMinStage ? stageRank(workspaceMinStage) : stageRank('alpha');
    return stageRank(stage) >= threshold;
}
/**
 * A command is available in a workspace when its stage is at or above the workspace's
 * min stage. Thin wrapper over isStageAvailable. Used by both the web menu filter and the
 * server start_run gate.
 */
export function isCommandAvailableAtStage(command, workspaceMinStage) {
    return isStageAvailable(command.stage, workspaceMinStage);
}
/**
 * Maturity stage of the SOW Template Studio (full-page document-first editor). Staged like
 * any not-yet-GA feature: 'alpha' = visible only where the workspace min stage is alpha
 * (local/dev). Promote to 'beta' to expose it in uat/prod.
 */
export const SOW_STUDIO_STAGE = 'alpha';
/**
 * Whether the SOW Studio is enabled for a workspace. A per-workspace `features.sow_studio`
 * override wins when set (true/false - lets a deployment enable the studio independently of
 * the stage); absent (null/undefined) falls back to the SOW_STUDIO_STAGE gate. Used by BOTH
 * the web route/launcher gate and the server's "open in studio" link, so the two resolve
 * identically from the same per-workspace inputs (no build-flag agreement gotcha).
 */
export function resolveSowStudioEnabled(override, workspaceMinStage) {
    if (override != null)
        return override;
    return isStageAvailable(SOW_STUDIO_STAGE, workspaceMinStage);
}
/**
 * Maturity stage of the client staffing editor (Workspace -> Resources -> Staffing:
 * which roles staff a product's deals and the split). 'alpha' = local/dev by default;
 * a per-workspace `features.staffing_editor` override exposes it in uat/prod.
 */
export const STAFFING_EDITOR_STAGE = 'alpha';
/**
 * Whether the staffing editor is enabled for a workspace. A `features.staffing_editor`
 * override (true/false) wins; absent falls back to the STAFFING_EDITOR_STAGE gate. Used by
 * the web (show the grid) and the API (refuse the routes), so both resolve identically.
 */
export function resolveStaffingEditorEnabled(override, workspaceMinStage) {
    if (override != null)
        return override;
    return isStageAvailable(STAFFING_EDITOR_STAGE, workspaceMinStage);
}
/**
 * Maturity stage of the scope-list upload (docs/plans/27): upload a scope file on a deal, get the
 * scope items + an estimate. 'alpha' = visible only where the workspace min stage is alpha
 * (local/dev), so a deployment exposes it ONLY via the per-workspace override below.
 */
export const SCOPE_UPLOAD_STAGE = 'alpha';
/**
 * Whether the scope-list upload is enabled for a workspace. Same shape as
 * {@link resolveSowStudioEnabled}: a per-workspace `features.scope_upload` override (true/false)
 * wins when set, else the stage gate applies. This is what makes the feature single-workspace
 * (RCP first) in a deployment whose other workspaces sit at the same min stage - `stage` alone
 * cannot do that, because it gates by DEPLOYMENT, not by workspace. Used by BOTH the web (button
 * visibility) and the server (route refusal), so the two resolve identically.
 */
export function resolveScopeUploadEnabled(override, workspaceMinStage) {
    if (override != null)
        return override;
    return isStageAvailable(SCOPE_UPLOAD_STAGE, workspaceMinStage);
}
/**
 * Maturity stage of the deal Resource Plan tab (staffing table + Gantt). 'alpha' = visible only
 * where the workspace min stage is alpha (local/dev), so a beta/ga deployment exposes it per
 * workspace via the override below (Echo Park, 915 Advisory first).
 */
export const RESOURCE_PLAN_STAGE = 'alpha';
/**
 * Whether the deal Resource Plan tab is shown for a workspace. Same shape as
 * {@link resolveSowStudioEnabled}: a per-workspace `features.resource_plan` override (true/false)
 * wins when set, else the RESOURCE_PLAN_STAGE gate applies.
 */
export function resolveResourcePlanEnabled(override, workspaceMinStage) {
    if (override != null)
        return override;
    return isStageAvailable(RESOURCE_PLAN_STAGE, workspaceMinStage);
}
/**
 * Maturity stage of the deal Scope tab (the questionnaire as a Tiptap document). 'alpha' = visible
 * only where the workspace min stage is alpha (local/dev); a workspace can opt out (or in) via the
 * override below - Echo Park hides it even on local (Harman, 2026-10-20).
 */
export const SCOPE_TAB_STAGE = 'alpha';
/**
 * Whether the deal Scope tab is shown for a workspace. Same shape as
 * {@link resolveResourcePlanEnabled}: a per-workspace `features.scope_tab` override (true/false)
 * wins when set, else the SCOPE_TAB_STAGE gate applies.
 */
export function resolveScopeTabEnabled(override, workspaceMinStage) {
    if (override != null)
        return override;
    return isStageAvailable(SCOPE_TAB_STAGE, workspaceMinStage);
}
//# sourceMappingURL=slash-commands.js.map